<script lang="ts">
  import { onMount } from 'svelte'
  import ActualTooltip from './ActualTooltip.svelte'
  import type { Position, Style } from './types'
  import { browser, dev } from '$app/env'

  interface Props {
    position?: Position
    showOn?: 'hover' | 'click' | 'create'
    style?: Style
    /** Whether the tooltip should stay around after the mouse moves away */
    linger?: boolean
    lingerDuration?: number
    delay?: number
    children?: import('svelte').Snippet
  }

  let {
    position = 'center-bottom',
    showOn = 'hover',
    style = 'default',
    linger = false,
    lingerDuration = 200,
    delay = 0,
    children,
  }: Props = $props()

  // svelte-ignore state_referenced_locally
  let visible = $state(showOn === 'create')
  let timeout: number | undefined = undefined

  const onMouseEnter = () => {
    // Only called when showOn = hover
    window.clearTimeout(timeout)
    if (delay) {
      timeout = window.setTimeout(() => (visible = true), delay)
    } else {
      visible = true
    }
  }
  const onMouseLeave = () => {
    // Only called when showOn = hover
    window.clearTimeout(timeout)
    if (linger) {
      timeout = window.setTimeout(() => (visible = false), lingerDuration)
    } else {
      visible = false
    }
  }
  const onClick = () => {
    // Only called when showOn = click
    if (!visible) {
      visible = true
    }
  }

  let tooltipElement: HTMLSpanElement | undefined = $state()

  const onOutsideClick = () => {
    // Only called when showOn = click
    visible = false
  }

  const onKeyDown = (event: KeyboardEvent) => {
    // Only called when showOn = click
    if (visible && event.key === 'Escape') {
      visible = false
    }
  }
  const onDocumentClick = (event: MouseEvent) => {
    const parent = tooltipElement!.parentElement
    if (!parent) return

    if (
      !parent.contains(event.target as HTMLElement) &&
      !tooltipElement!.contains(event.target as HTMLElement)
    ) {
      onOutsideClick()
    }
  }

  let parent: HTMLElement | null = $state(null)

  $effect(() => {
    if (browser && visible && showOn === 'click') {
      document.addEventListener('click', onDocumentClick, true)
      document.addEventListener('keydown', onKeyDown, true)
    } else if (browser && !visible && showOn === 'click') {
      document.removeEventListener('click', onDocumentClick, true)
      document.removeEventListener('keydown', onKeyDown, true)
    }
  })

  onMount(() => {
    parent = tooltipElement!.parentElement

    if (!parent) {
      console.error('Tooltip must have a parent')
      return
    }

    if (dev) {
      if (window.getComputedStyle(parent).position === 'static') {
        const alertElement = document.createElement('small')
        alertElement.innerHTML =
          ' <strong style="color: white; background-color: #f44; font-family: monospace;">Tooltip must have a parent with a non-static (absolute, relative, ...) position</strong>'

        parent.appendChild(alertElement)
      }
    }

    if (showOn === 'click') {
      parent?.addEventListener('click', onClick)
    }
    if (showOn === 'hover') {
      parent?.addEventListener('mouseenter', onMouseEnter)
      parent?.addEventListener('mouseleave', onMouseLeave)
    }

    return () => {
      parent?.removeEventListener('click', onClick)
      parent?.removeEventListener('mouseenter', onMouseEnter)
      parent?.removeEventListener('mouseleave', onMouseLeave)

      document.removeEventListener('click', onDocumentClick, true)
      document.removeEventListener('keydown', onKeyDown, true)
    }
  })
</script>

<span class="tooltip-observer" bind:this={tooltipElement}></span>

{#if visible && parent}
  <ActualTooltip {position} {style}>
    {@render children?.()}
  </ActualTooltip>
{/if}

<style lang="postcss">
  .tooltip-observer {
    display: none;
  }
</style>
